import { ExternalLink, Flag, ThumbsDown, ThumbsUp } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { type AnswerFeedback as SavedFeedback, AnswerRating } from '@/lib/agent/answerFeedback'
import { cn } from '@/utils/cn'
import { AnswerCommentBox, type CommentDraft } from './AnswerCommentBox'

const ACTIVE_STYLE: Record<AnswerRating, string> = {
  [AnswerRating.Up]: 'bg-brand/15 text-ink',
  [AnswerRating.Down]: 'bg-brand/15 text-ink',
  [AnswerRating.Flag]: 'bg-destructive/10 text-destructive',
}

/**
 * Staff rate one agent answer: thumbs up, thumbs down, or a harmful flag with a reason.
 * Each chat passes its own save and clear calls; both resolve once the server has the change.
 */
export function AnswerFeedback({
  feedback,
  traceUrl,
  onSave,
  onClear,
  className,
}: {
  feedback: SavedFeedback | null
  traceUrl: string | null | undefined
  onSave: (feedback: SavedFeedback) => Promise<unknown>
  onClear: () => Promise<unknown>
  className?: string
}) {
  const [draft, setDraft] = useState<CommentDraft | null>(null)
  const [pending, setPending] = useState(false)

  const run = async (change: () => Promise<unknown>) => {
    setPending(true)
    try {
      await change()
      return true
    } catch {
      toast.error("Couldn't save your feedback. Try again.")
      return false
    } finally {
      setPending(false)
    }
  }

  const choose = (rating: AnswerRating) => {
    if (feedback?.rating === rating) {
      setDraft(null)
      void run(onClear)
      return
    }
    const comment = feedback?.comment ?? ''
    // A flag waits for its reason; the thumbs save at once and offer the comment box after.
    if (rating === AnswerRating.Flag) {
      setDraft({ rating, flagReason: '', comment })
      return
    }
    void run(() => onSave({ rating, flagReason: '', comment }))
    setDraft(rating === AnswerRating.Down ? { rating, flagReason: '', comment } : null)
  }

  const saveDraft = async (current: CommentDraft) => {
    if (await run(() => onSave(current))) setDraft(null)
  }

  const choiceButton = (rating: AnswerRating, label: string, icon: React.ReactNode) => {
    const active = feedback?.rating === rating || draft?.rating === rating
    return (
      <Button
        variant="ghost"
        size="xs"
        aria-label={label}
        aria-pressed={feedback?.rating === rating}
        className={cn('text-ink-500', active && ACTIVE_STYLE[rating])}
        disabled={pending}
        onClick={() => choose(rating)}
      >
        {icon}
        {rating === AnswerRating.Flag && 'Flag'}
      </Button>
    )
  }

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex flex-wrap items-center gap-1">
        {choiceButton(AnswerRating.Up, 'Good answer', <ThumbsUp />)}
        {choiceButton(AnswerRating.Down, 'Poor answer', <ThumbsDown />)}
        {choiceButton(AnswerRating.Flag, 'Flag as harmful', <Flag />)}
        {feedback && !draft && (
          <Button
            variant="link"
            size="xs"
            onClick={() =>
              setDraft({
                rating: feedback.rating,
                flagReason: feedback.flagReason,
                comment: feedback.comment,
              })
            }
          >
            {feedback.comment ? 'Edit comment' : 'Add comment'}
          </Button>
        )}
        {traceUrl && (
          <Button variant="ghost" size="xs" className="text-ink-500 ml-auto" href={traceUrl} target="_blank">
            <ExternalLink />
            View trace
          </Button>
        )}
      </div>
      {draft && (
        <AnswerCommentBox
          draft={draft}
          onChange={setDraft}
          onCancel={() => setDraft(null)}
          onSave={() => void saveDraft(draft)}
          saving={pending}
        />
      )}
    </div>
  )
}
